import React, { useRef, useState } from 'react'
import { jsPDF } from 'jspdf'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetItem } from './hooks'
import { Fetching } from '../../../components/Fetching'
import { formatDateMMDDYYYY, formatTime, formattedDate } from '../../../lib/time'
import { DailyReportFormExportNew } from './DailyReportFormExportNew'
import html2canvas from 'html2canvas'
import { BackButton } from '../../../components/navigation/BackButton'

// Create Document Component
export const DailyReportFormExport = (props: any) => {
  const { id, reportId } = useParams()
  const { data, isFetching } = useGetItem('daily-report', reportId)
  const pdfRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const dailyReportRef = useRef({}) as React.MutableRefObject<any>
  const [isDownloadPdf, setIsDownloadPdf] = useState(false)

  const mapTableData = (timesheets: any) => {
    return timesheets.map((timesheet: any) => [
      `${timesheet.user.firstName} ${timesheet.user.lastName}`,
      formatDateMMDDYYYY(timesheet.formattedTimeDurations.startTime),
      timesheet.formattedTimeDurations.totalTimeWorked,
      timesheet.formattedTimeDurations.regularTimeWorked,
      timesheet.formattedTimeDurations.overTimeWorked,
      timesheet.formattedTimeDurations.doubleOverTimeWorked,
      timesheet.costCodes
        .map((code: any) => `${code.percentage}% - ${code.costCode.label}`)
        .join(', '),
    ])
  }
 
  const handleExport = async () => {
    await setIsDownloadPdf(true)
    const input: any = dailyReportRef.current

    html2canvas(input).then((canvas: any) => {
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      })
      // Track the current page
      let currentPage = 1
      let totalPages = 1

      // Function to add a new page
      const addNewPage = (position: number) => {
        pdf.addPage()
        pdf.addImage(
          imgData,
          'PNG',
          xOffset,
          position ? -(position) : -(pdfHeight - 0),
          widthToFit,
          heightToFit
        )
        currentPage++
        // addFooter();
      }

      const addFooter = () => {
        const pages = totalPages
        pdf.setFontSize(6)
        pdf.setTextColor(151, 154, 171)
        pdf.text(
          `Page ${currentPage} of ${pages}`,
          pdf.internal.pageSize.getWidth() - 16,
          pdf.internal.pageSize.getHeight() - 3
        )
        pdf.text(
          `Created by ${data?.clientName} with Autodesk® Construction CloudTM on ${formattedDate(data.date)} at ${formatTime(data.date)} PM EDT`,
          5,
          pdf.internal.pageSize.getHeight() - 3
        )
      }

      const imgProps = pdf.getImageProperties(imgData)
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight() // Use A4 page height

      let widthToFit: any,
        heightToFit: any,
        xOffset = 0,
        yOffset = 0

      // Check if the image needs to be scaled
      if (imgProps.width > pdfWidth) {
        widthToFit = pdfWidth
        heightToFit = (imgProps.height * pdfWidth) / imgProps.width
      } else {
        widthToFit = imgProps.width
        heightToFit = imgProps.height
      }

      // Check if the scaled image fits on the current page
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, widthToFit, heightToFit)

      let heightLeft = heightToFit - pdfHeight
      let newPagePosition = pdfHeight

      while (heightLeft >= 0) {
        totalPages++
        addNewPage(newPagePosition)
        newPagePosition += pdfHeight
        heightLeft -= pdfHeight
      }

      pdf.save(`daily-report-${data.project.projectName}-${reportId}.pdf`)

      setIsDownloadPdf(false)
    })
  }

  if (isFetching) {
    return <Fetching />
  }

  return (
    <>
      {data ? (
        <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
            <BackButton steps={-1} />
            <Button
              sx={{ marginLeft: 2, marginBottom: 5, cursor: 'pointer' }}
              variant='contained'
              onClick={handleExport}>
              Download PDF
            </Button>
          </Box>
          <Box
            ref={pdfRef}
            sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
            <Typography variant='h4' gutterBottom>
              Daily Report
            </Typography>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Box>
                  <strong>Project No.:</strong> {data.project.projectNumber}
                </Box>
                <Box>
                  <strong>Report No.:</strong>{' '}
                  {data.reportNumber
                    ? new Intl.NumberFormat('en', {
                      minimumIntegerDigits: 6,
                      useGrouping: false,
                    }).format(data.reportNumber)
                    : ''}
                </Box>
                <Box>
                  <strong>Date:</strong> {data.date}
                </Box>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                General Information
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <Box>
                  <strong>Project:</strong> {data.project.projectName}
                </Box>
                <Box>
                  <strong>Location:</strong> {data.project.locations[0].address}
                </Box>
                <Box>
                  <strong>Weather:</strong> {data.weather}
                </Box>
                <Box>
                  <strong>Client:</strong> {data.project.clientName}
                </Box>
                <Box>
                  <strong>Service:</strong> {data.project.service}
                </Box>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Box>
                  <strong>Foreman:</strong> {data.foreman}
                </Box>
                <Box>
                  <strong>Signature: </strong>{data?.foremanSignature}
                </Box>
              </Box>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Box>
                  <strong>Project Manager:</strong> {data.projectManager}
                </Box>
                <Box>
                  <strong>Signature: </strong>{data?.projectManagerSignature}
                </Box>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Equipment
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Equipment Supplier</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.equipment && data.equipment.length ? (
                        data.equipment.map((item: any, index: number) => {
                          return (
                            <TableRow key={`equipment${index}`}>
                              <TableCell>{item.lineItemValue}</TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>{item.supplier}</TableCell>
                            </TableRow>
                          )
                        })
                      ) : (
                        <TableRow key={'timesheet0'}>
                          <TableCell colSpan={3}>
                            No equipment was input for today.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Material Delivered to Site Today
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Material Supplier</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.materialsDelivered &&
                        data.materialsDelivered.length ? (
                        data.materialsDelivered.map(
                          (item: any, index: number) => {
                            return (
                              <TableRow key={`material${index}`}>
                                <TableCell>{item.lineItemValue}</TableCell>
                                <TableCell>{item.quantity}</TableCell>
                                <TableCell>{item.supplier}</TableCell>
                              </TableRow>
                            )
                          }
                        )
                      ) : (
                        <TableRow key={'material0'}>
                          <TableCell colSpan={3}>
                            No materials were delivered today.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Work Performed Today
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }} style={{ whiteSpace: 'pre-wrap' }}>
                {data.workPerformed}
              </Box>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Causing Delays in Progress
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                {/* {data.delaysAndImpacts} */}
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.delaysAndImpacts && data.delaysAndImpacts.length ? (
                        data.delaysAndImpacts.map(
                          (item: any, index: number) => {
                            return (
                              <TableRow key={`delay${index}`}>
                                <TableCell>{item.lineItemValue}</TableCell>
                              </TableRow>
                            )
                          }
                        )
                      ) : (
                        <TableRow key={'delay0'}>
                          <TableCell>No delays/impacts.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </Box>
          <Box>
            <Box sx={{ borderTop: 1, borderColor: 'primary.main', mt: 2 }}>
              <Typography sx={{ mt: 2 }} variant='h6' gutterBottom>
                Timesheets
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Total Hours</TableCell>
                        <TableCell>Regular Hours</TableCell>
                        <TableCell>Overtime Hours</TableCell>
                        <TableCell>Doubletime Hours</TableCell>
                        <TableCell>Cost Code</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.timesheets ? (
                        data.timesheets.map((timesheet: any, index: number) => {
                          return (
                            <TableRow key={`timesheet${index}`}>
                              <TableCell width='180'>
                                {timesheet.user?.firstName?timesheet.user?.firstName:"---"}
                                {timesheet.user?.lastName?timesheet.user?.lastName:"---"}
                              </TableCell>
                              {/* <TableCell>{timesheet.totalTimeWorkedInHours ? timesheet.totalTimeWorkedInHours.toFixed(2) : ''}</TableCell> */}
                              <TableCell width='150'>
                                {formatDateMMDDYYYY(
                                  timesheet?.formattedTimeDurations?.startTime?timesheet?.formattedTimeDurations?.startTime:"Open"
                                )}
                              </TableCell>
                              <TableCell>
                                {
                                  timesheet?.formattedTimeDurations?.totalTimeWorked?timesheet?.formattedTimeDurations?.totalTimeWorked:"Open"
                                }
                              </TableCell>
                              <TableCell>
                                {
                                  timesheet?.formattedTimeDurations?.regularTimeWorked?timesheet?.formattedTimeDurations?.regularTimeWorked:"Open"
                                }
                              </TableCell>
                              <TableCell>
                                {
                                  timesheet?.formattedTimeDurations?.overTimeWorked?timesheet?.formattedTimeDurations?.overTimeWorked:"Open"
                                }
                              </TableCell>
                              <TableCell>
                                {
                                  timesheet?.formattedTimeDurations?.doubleOverTimeWorked?timesheet?.formattedTimeDurations?.doubleOverTimeWorked:"Open"
                                }
                              </TableCell>
                              <TableCell>
                                {timesheet?.costCodes?.length > 0
                                  ? timesheet?.costCodes.map(
                                    (costCode: any) =>
                                      `${costCode.percentage}% - ${costCode.costCode.label} `
                                  )
                                  : "Open"}
                              </TableCell>
                            </TableRow>
                          )
                        })
                      ) : (
                        <TableRow key={'timesheet0'}>
                          <TableCell>
                            No timesheets were found for today.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}
      <Box
        ref={dailyReportRef}
        sx={{ display: isDownloadPdf ? 'block' : 'none' }}>
        <DailyReportFormExportNew />
      </Box>
    </>
  )
}
